import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api, fmt } from "../lib/api";
import { Package, Save, Trash2, Plus, RefreshCw } from "lucide-react";

const today = () => new Date().toISOString().split("T")[0];

function emptyForm() {
  return {
    date: today(),
    vendor_name: "",
    tank_location: "",
    quantity_kg: "",
    rate_per_kg: "",
    challan_no: "",
    notes: "",
  };
}

export default function BulkPurchaseEntryPage() {
  const { company } = useAuth();
  const [form, setForm]         = useState(emptyForm());
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editId, setEditId]     = useState(null);
  const [error, setError]       = useState("");

  const totalAmount = parseFloat(form.quantity_kg || 0) * parseFloat(form.rate_per_kg || 0);

  async function fetchRecords() {
    setLoading(true);
    try {
      const data = await api("/bulk-purchases?limit=200");
      setRecords(data.rows || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchRecords(); }, []);

  function setField(k, v) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (!form.quantity_kg || !form.rate_per_kg) {
      setError("Quantity and Rate are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_kg: parseFloat(form.quantity_kg),
        rate_per_kg: parseFloat(form.rate_per_kg),
      };
      if (editId) {
        await api(`/bulk-purchases/${editId}`, { method: "PATCH", body: payload });
      } else {
        await api("/bulk-purchases", { method: "POST", body: payload });
      }
      setSaved(true);
      setForm(emptyForm());
      setEditId(null);
      setTimeout(() => setSaved(false), 2500);
      fetchRecords();
    } catch (err) {
      setError(err.message || "Failed to save entry.");
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(rec) {
    setForm({
      date: rec.date,
      vendor_name: rec.vendor_name || "",
      tank_location: rec.tank_location || "",
      quantity_kg: String(rec.quantity_kg),
      rate_per_kg: String(rec.rate_per_kg),
      challan_no: rec.challan_no || "",
      notes: rec.notes || "",
    });
    setEditId(rec.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this bulk purchase entry?")) return;
    try {
      await api(`/bulk-purchases/${id}`, { method: "DELETE" });
      fetchRecords();
    } catch (err) {
      alert(err.message || "Delete failed.");
    }
  }

  const inp = "w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white";

  return (
    <div className="px-6 py-6 max-w-3xl" data-testid="bulk-purchase-page">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
        <Package size={11} /> Entry
      </div>
      <h1 className="font-black text-3xl tracking-tight mb-1">Bulk Purchase (Bullet)</h1>
      <p className="text-sm text-gray-500 mb-6">Record bulk LPG purchases in KG from large tank/bullet suppliers.</p>

      {/* Entry Form */}
      <form onSubmit={handleSave} data-testid="bulk-purchase-form">
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="small-label block mb-1.5">Date</label>
              <input
                data-testid="bp-date"
                className={inp}
                type="date"
                value={form.date}
                onChange={e => setField("date", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="small-label block mb-1.5">Tank / Location</label>
              <input
                data-testid="bp-tank"
                className={inp}
                type="text"
                placeholder="e.g. Pune Bullet Tank"
                value={form.tank_location}
                onChange={e => setField("tank_location", e.target.value)}
              />
            </div>
            <div>
              <label className="small-label block mb-1.5">Vendor / Supplier Name</label>
              <input
                data-testid="bp-vendor"
                className={inp}
                type="text"
                placeholder="Vendor name"
                value={form.vendor_name}
                onChange={e => setField("vendor_name", e.target.value)}
              />
            </div>
            <div>
              <label className="small-label block mb-1.5">Challan No.</label>
              <input
                data-testid="bp-challan"
                className={inp}
                type="text"
                placeholder="e.g. BP-001"
                value={form.challan_no}
                onChange={e => setField("challan_no", e.target.value)}
              />
            </div>
            <div>
              <label className="small-label block mb-1.5">Quantity (KG)</label>
              <input
                data-testid="bp-quantity"
                className={inp}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.quantity_kg}
                onChange={e => setField("quantity_kg", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="small-label block mb-1.5">Rate (₹/KG)</label>
              <input
                data-testid="bp-rate"
                className={inp}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.rate_per_kg}
                onChange={e => setField("rate_per_kg", e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="small-label block mb-1.5">Notes (optional)</label>
            <input
              className={inp}
              type="text"
              placeholder="Any remarks..."
              value={form.notes}
              onChange={e => setField("notes", e.target.value)}
            />
          </div>

          {/* Total preview */}
          {totalAmount > 0 && (
            <div className="bg-gray-50 rounded-md px-4 py-3 flex items-center justify-between border border-gray-100">
              <span className="text-sm text-gray-600">
                {form.quantity_kg} KG × ₹{form.rate_per_kg}/KG
              </span>
              <span className="font-bold text-lg">
                = ₹{fmt(totalAmount)}
              </span>
            </div>
          )}
        </div>

        {error && (
          <p className="text-red-600 text-sm mb-3" data-testid="bp-error">{error}</p>
        )}

        <div className="flex justify-end gap-3">
          {editId && (
            <button
              type="button"
              className="btn-secondary flex items-center gap-2"
              onClick={() => { setForm(emptyForm()); setEditId(null); setError(""); }}
              data-testid="bp-cancel-edit"
            >
              Cancel Edit
            </button>
          )}
          {saved && (
            <span className="text-green-600 text-sm font-medium self-center" data-testid="bp-saved-msg">
              Saved!
            </span>
          )}
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="bp-save-btn"
          >
            <Save size={14} />
            {saving ? "Saving…" : editId ? "Update Entry" : "Save Entry"}
          </button>
        </div>
      </form>

      {/* Records Table */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg">Recent Bulk Purchases</h2>
          <button
            onClick={fetchRecords}
            className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
            data-testid="bp-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : records.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-400 text-sm">
            No bulk purchase entries yet. Save your first entry above.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="bp-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {["Date", "Vendor", "Tank/Location", "Qty (KG)", "Rate", "Total", "Challan", ""].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map(rec => (
                  <tr key={rec.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-700">{rec.date}</td>
                    <td className="px-4 py-2.5">{rec.vendor_name || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-2.5">{rec.tank_location || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-2.5 font-mono">{rec.quantity_kg}</td>
                    <td className="px-4 py-2.5 font-mono">₹{rec.rate_per_kg}</td>
                    <td className="px-4 py-2.5 font-semibold">₹{fmt(rec.total_amount)}</td>
                    <td className="px-4 py-2.5 text-gray-500">{rec.challan_no || "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(rec)}
                          className="text-blue-500 hover:text-blue-700 text-xs"
                          data-testid={`bp-edit-${rec.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(rec.id)}
                          className="text-gray-300 hover:text-red-500"
                          data-testid={`bp-del-${rec.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
